import { Box, CircularProgress } from "@mui/material";
import { postRequest } from "@platformx/authoring-apis";
import {
  RootState,
  updateContentForCard,
  updateDynamicPrelemAssetInfo,
  updateLivestreamPrelemAssetInfo,
  updateSetting,
} from "@platformx/authoring-state";
import { ShowToastError } from "@platformx/utilities";
import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import BackButton from "../BackButton/BackButton";
import { PrelemSettingList } from "../utils/constants";
import { parseObject } from "../utils/helper";

// Lazy load all settings components - they're conditionally rendered based on pageId
const AdditionalSettings = lazy(() => import("./AdditionalSettings/AdditionalSettings"));
const CardLimitSetting = lazy(() => import("./AdditionalSettings/CardLimitSetting"));
const CDPFilter = lazy(() => import("./CdpFilter"));
const ColorSettings = lazy(() => import("./ColorSettings/ColorSettings"));
const SingleDatePicker = lazy(() => import("./CommonItems/SingleDatePicker"));
const SingleTextBox = lazy(() => import("./CommonItems/SingleTextBox"));
const GamAdSetting = lazy(() => import("./GamAdSetting"));
const GoogleAdSetting = lazy(() => import("./GoogleAdSetting"));
const Personalisation = lazy(() => import("./Personalisation"));
const PrelemAdvanced = lazy(() => import("./prelemAdvanced"));
const PrelemAnalytics = lazy(() => import("./prelemAnalytics"));
const PrelemBrightcoveVideo = lazy(() => import("./PrelemBrightcoveVideo"));
const PrelemEcom = lazy(() => import("./PrelemEcom"));
const PrelemImages = lazy(() => import("./PrelemImages"));
const PrelemSEO = lazy(() => import("./PrelemSEO"));
const PrelemSettings = lazy(() => import("./PrelemSettings/PrelemSettings"));
const PrelemTestimonials = lazy(() => import("./PrelemTestimonials"));
const PrelemTwitter = lazy(() => import("./PrelemTwitter"));
const PrelemVideos = lazy(() => import("./PrelemVideos"));
const SponsorSetting = lazy(() => import("./SponsorSetting"));

// Loading fallback for lazy components
const LazyFallback = () => (
  <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
    <CircularProgress size={24} />
  </Box>
);

const PrelemSettingsCard = ({
  selectedPrelemIndex,
  pageId,
  setPageId,
  updatePrelemContent,
  imageSettingRef,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { page } = useSelector((state: RootState) => state);
  const [prelemModelData, setPrelemModelData] = useState(
    parseObject(page.prelemMetaArray[selectedPrelemIndex]),
  );

  const handleSave = async (sectionToUpdate, data, index) => {
    // Create a shallow copy of the page object to avoid direct mutation
    const updatedPage = { ...page };

    // Create a shallow copy of the prelemMetaArray to avoid direct mutation
    const updatedPrelemMetaArray = [...updatedPage.prelemMetaArray];

    // Create a shallow copy of the selected prelem object to avoid direct mutation
    const selectedPrelem = { ...updatedPrelemMetaArray[selectedPrelemIndex] };

    // Create a shallow copy of the content object to avoid direct mutation
    const selectedContent = { ...selectedPrelem.content };

    if (sectionToUpdate === "EcomHandle") {
      // Dispatch an action to update the dynamic prelem asset info in the state
      dispatch(
        updateDynamicPrelemAssetInfo({
          selectedPrelemIndex: selectedPrelemIndex,
          sectionToUpdate: sectionToUpdate,
          data: data,
        }),
      );

      // Update the content with the new EcomHandle data
      selectedPrelem.content = {
        ...selectedContent,
        ApiEndPoint: data.apiEndPoint,
        OauthEndPoint: data.oauthEndPoint,
        Password: data.password,
        Username: data.userName,
      };
    } else if (sectionToUpdate === "TwitterHandle") {
      // Dispatch an action to update the dynamic prelem asset info in the state
      dispatch(
        updateDynamicPrelemAssetInfo({
          selectedPrelemIndex: selectedPrelemIndex,
          sectionToUpdate: sectionToUpdate,
          data: data,
        }),
      );

      // Update the content with the new TwitterHandle data
      selectedPrelem.content = {
        ...selectedContent,
        TwitterHandle: data,
      };
    } else if (sectionToUpdate === "Testimonials") {
      // Dispatch an action to update the dynamic prelem asset info in the state
      dispatch(
        updateDynamicPrelemAssetInfo({
          selectedPrelemIndex: selectedPrelemIndex,
          sectionToUpdate: sectionToUpdate,
          data: data,
        }),
      );

      // Update the content with the new Testimonials data
      selectedPrelem.content = {
        ...selectedContent,
        Testimonials: data,
      };
    } else if (sectionToUpdate === "Livestream") {
      // Dispatch an action to update the livestream prelem asset info in the state
      dispatch(
        updateLivestreamPrelemAssetInfo({
          selectedPrelemIndex: selectedPrelemIndex,
          sectionToUpdate: sectionToUpdate,
          data: data,
        }),
      );

      // Update the content with the new Livestream data
      selectedPrelem.content = {
        ...selectedContent,
        ...data,
      };
    } else if (sectionToUpdate === "ImageCompound") {
      // Dispatch an action to update the content for the specific card in the state
      dispatch(
        updateContentForCard({
          selectedPrelemIndex: selectedPrelemIndex,
          sectionToUpdate: sectionToUpdate,
          data: data.ImageCompound,
          index: index,
        }),
      );
    } else if (sectionToUpdate === "Videos") {
      // Perform any required processing for video updatesbitstream
      if (data?.bitstreamId) await autoCrop({ data, sectionToUpdate, index });
      else {
        //Added only for youtube video case - If this affect other video prelems then remove the if statement and delete the else statement.
        dispatch(
          updateContentForCard({
            selectedPrelemIndex: selectedPrelemIndex,
            sectionToUpdate: sectionToUpdate,
            data: data,
            index: index,
          }),
        );
        const updateContent = {
          ...page?.prelemMetaArray[selectedPrelemIndex]?.content,
          [sectionToUpdate]: {
            ...page?.prelemMetaArray[selectedPrelemIndex]?.content[sectionToUpdate],
            [index]: data,
          },
        };
        updatePrelemContent(
          updateContent,
          selectedPrelemIndex,
          prelemModelData.DocumentPath,
          prelemModelData.DocumentCreationPath,
          prelemModelData.DocumentType,
          prelemModelData.InstanceId,
        );
      }
    } else if (sectionToUpdate === "rpikeyupdate") {
      // Dispatch an action to update the dynamic prelem asset info in the state
      dispatch(
        updateDynamicPrelemAssetInfo({
          selectedPrelemIndex: selectedPrelemIndex,
          sectionToUpdate: sectionToUpdate,
          data: data,
        }),
      );

      // Update the content with the new RpiKey data
      selectedPrelem.content = {
        ...selectedContent,
        RpiKey: data.rpiKey,
      };
    } else if (
      sectionToUpdate === "selected_personalisation" ||
      sectionToUpdate === "selected_prelem_bg_color" ||
      sectionToUpdate === "analytics" ||
      sectionToUpdate === "form_end_date" ||
      sectionToUpdate === "form_id" ||
      sectionToUpdate === "form_version" ||
      sectionToUpdate === "additional_setting" ||
      sectionToUpdate === "select_item_count_x_str" ||
      sectionToUpdate === "google_ad_setting" ||
      sectionToUpdate === "sponsor_settings"
    ) {
      // Dispatch an action to update the dynamic prelem asset info in the state
      dispatch(
        updateSetting({
          selectedPrelemIndex: selectedPrelemIndex,
          sectionToUpdate: sectionToUpdate,
          data: data,
        }),
      );
      // Update the content with the new sectionToUpdate data
      selectedPrelem.content = {
        ...selectedContent,
        [sectionToUpdate]: data,
      };
    } else {
      // Dispatch an action to update the content for the specific card in the state
      dispatch(
        updateContentForCard({
          selectedPrelemIndex: selectedPrelemIndex,
          sectionToUpdate: sectionToUpdate,
          data: data,
          index: index,
        }),
      );
    }

    // Reassign the updated prelem to the cloned array
    updatedPrelemMetaArray[selectedPrelemIndex] = selectedPrelem;
    // Reassign the updated prelemMetaArray to the page object
    updatedPage.prelemMetaArray = updatedPrelemMetaArray;

    // Update the prelem content if the section matches specific cases
    if (
      [
        "ImageCompound",
        "TwitterHandle",
        "Livestream",
        "Testimonials",
        "rpikeyupdate",
        "selected_personalisation",
        "analytics",
        "selected_prelem_bg_color",
        "form_end_date",
        "form_id",
        "form_version",
        "additional_setting",
        "select_item_count_x_str",
        "google_ad_setting",
        "prelem_settings",
        "sponsor_settings",
      ].includes(sectionToUpdate)
    ) {
      let updateContent = "";

      if (sectionToUpdate === "ImageCompound" || sectionToUpdate === "Videos") {
        // Update content for ImageCompound or Videos
        updateContent = {
          ...selectedPrelem.content,
          [sectionToUpdate]: {
            ...selectedPrelem.content[sectionToUpdate],
            [index]: sectionToUpdate === "ImageCompound" ? data.ImageCompound : data,
          },
        };
      } else {
        updateContent = selectedPrelem.content;
      }

      // Perform the final update of prelem content
      updatePrelemContent(
        updateContent,
        selectedPrelemIndex,
        prelemModelData.DocumentPath,
        prelemModelData.DocumentCreationPath,
        prelemModelData.DocumentType,
        prelemModelData.InstanceId,
        prelemModelData.rpiKey,
      );
    } else {
      return;
    }
  };

  const autoCrop = async ({ data, sectionToUpdate, index }) => {
    const bitstreamId = data?.bitstreamId;
    const payload = {
      bitstreamId,
      visibility: "public",
    };
    try {
      const response = await postRequest("api/v1/assets/image/auto-crop", payload);
      if (response && response?.bitstream_id) {
        const updatedData = { ...data, Thumbnail: JSON.stringify(response), ext: response.ext };
        dispatch(
          updateContentForCard({
            selectedPrelemIndex: selectedPrelemIndex,
            sectionToUpdate: sectionToUpdate,
            data: updatedData,
            index: index,
          }),
        );
        const updateContent = {
          ...page?.prelemMetaArray[selectedPrelemIndex]?.content,
          [sectionToUpdate]: {
            ...page?.prelemMetaArray[selectedPrelemIndex]?.content[sectionToUpdate],
            [index]: updatedData,
          },
        };
        updatePrelemContent(
          updateContent,
          selectedPrelemIndex,
          prelemModelData.DocumentPath,
          prelemModelData.DocumentCreationPath,
          prelemModelData.DocumentType,
          prelemModelData.InstanceId,
        );
      } else {
        ShowToastError(`${t("auto_cropping_failed")}`);
      }
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };
  useEffect(() => {
    setPrelemModelData(parseObject(page.prelemMetaArray[selectedPrelemIndex]));
  }, [page.prelemMetaArray[selectedPrelemIndex]]);

  const dynamicPrelemSetting = prelemModelData?.content?.dynamic_prelem_setting;
  const hasAllowedSelectionType = dynamicPrelemSetting?.allowed_selection_type !== undefined;

  return (
    <Box>
      <Suspense fallback={<LazyFallback />}>
        {prelemModelData?.PrelemId === "Prelem_066" && pageId === PrelemSettingList[0].id && (
          <PrelemEcom
            index={selectedPrelemIndex}
            EcomHandle={prelemModelData?.content}
            handleSave={handleSave}
            setPageId={setPageId}
          />
        )}
        {prelemModelData?.content?.TwitterHandle !== undefined &&
          pageId === PrelemSettingList[1].id && (
            <PrelemTwitter
              index={selectedPrelemIndex}
              twitterHandle={prelemModelData.content.TwitterHandle}
              handleSave={handleSave}
              setPageId={setPageId}
            />
          )}
        {prelemModelData?.content?.Testimonials !== undefined &&
          pageId === PrelemSettingList[2].id && (
            <PrelemTestimonials
              data={prelemModelData.content.Testimonials}
              sectionToUpdate='Testimonials'
              handleSave={handleSave}
              setPageId={setPageId}
            />
          )}
        {prelemModelData?.content?.ImageCompound !== undefined &&
          pageId === PrelemSettingList[3].id && (
            <Box className='pageSettingmainWp'>
              <BackButton setPageId={setPageId} Title={t("prelem_images")} backTo='prelemSetting' />
              {Object.entries(prelemModelData.content.ImageCompound).map((_, index) => {
                const PrelemId = prelemModelData?.PrelemId;
                const published_images =
                  prelemModelData?.content?.ImageCompound?.[`ImageCompound_${index + 1}`]
                    ?.published_images;

                return (
                  <PrelemImages
                    key={`ImageCompound_${index + 1}`}
                    index={`ImageCompound_${index + 1}`}
                    handleSave={handleSave}
                    cropType={prelemModelData?.content?.cropType}
                    aspectRatio={
                      published_images?.length === 1 && {
                        1440: published_images[0].aspect_ratio,
                        1280: published_images[0].aspect_ratio,
                        1024: published_images[0].aspect_ratio,
                        768: published_images[0].aspect_ratio,
                        600: published_images[0].aspect_ratio,
                        320: published_images[0].aspect_ratio,
                      }
                    }
                    sectionToUpdate='ImageCompound'
                    i={index}
                    prelemId={PrelemId}
                    imageSettingRef={imageSettingRef}
                    published_images={published_images}
                    original_image={
                      prelemModelData?.content?.ImageCompound?.[`ImageCompound_${index + 1}`]
                        ?.original_image
                    }
                  />
                );
              })}
            </Box>
          )}

        {prelemModelData?.content?.Videos !== undefined &&
          prelemModelData?.PrelemName !== "Image & Video Gallery" &&
          pageId === PrelemSettingList[4].id &&
          Object.entries(prelemModelData.content.Videos).map(([key, value]) => {
            return (
              <PrelemVideos
                key={key}
                index={key}
                playerFlow={prelemModelData?.content?.PlayerType}
                videoInstance={value}
                handleSave={handleSave}
                sectionToUpdate='Videos'
                setPageId={setPageId}
              />
            );
          })}
        {prelemModelData?.content?.PlayerType === "brightcove" &&
          pageId === PrelemSettingList[5].id && (
            <PrelemBrightcoveVideo
              setPageId={setPageId}
              index={selectedPrelemIndex}
              videoObj={{
                VideoID: prelemModelData?.content?.VideoID_x_str,
                PlayerID: prelemModelData?.content?.PlayerID,
                AccountID: prelemModelData?.content?.AccountID_x_str,
                brightcove_details: prelemModelData?.content?.brightcove_details,
              }}
              handleSave={handleSave}
              PrelemId={prelemModelData?.PrelemId}
            />
          )}
        {prelemModelData?.content?.TwitterHandle === undefined &&
          pageId === PrelemSettingList[6].id && (
            <PrelemSEO
              setPageId={setPageId}
              selectedPrelemIndex={selectedPrelemIndex}
              handleSave={handleSave}
            />
          )}

        {pageId === PrelemSettingList[7].id && <PrelemAdvanced setPageId={setPageId} />}
        {pageId === PrelemSettingList[8].id && (
          <PrelemAnalytics
            setPageId={setPageId}
            selectedPrelemIndex={selectedPrelemIndex}
            analyticsSettings={prelemModelData?.content?.analytics}
            sectionToUpdate='analytics'
            handleSave={handleSave}
          />
        )}
        {pageId === PrelemSettingList[9].id && (
          <CDPFilter
            setPageId={setPageId}
            index={selectedPrelemIndex}
            RpiKey={prelemModelData?.content.RpiKey}
            handleSave={handleSave}
          />
        )}
        {pageId === PrelemSettingList[10].id && (
          <Personalisation
            setPageId={setPageId}
            index={selectedPrelemIndex}
            sectionToUpdate='selected_personalisation'
            data={prelemModelData?.content?.personalisation}
            selectedPersonalisation={prelemModelData?.content?.selected_personalisation}
            handleSave={handleSave}
          />
        )}
        {pageId === PrelemSettingList[11].id && (
          <ColorSettings
            setPageId={setPageId}
            index={selectedPrelemIndex}
            sectionToUpdate='selected_prelem_bg_color'
            title='prelem_color'
            data={JSON.parse(JSON.parse(prelemModelData?.content?.prelem_bg_color))}
            selectedItem={prelemModelData?.content?.selected_prelem_bg_color}
            handleSave={handleSave}
          />
        )}
        {pageId === PrelemSettingList[12].id && (
          <SingleDatePicker
            setPageId={setPageId}
            index={selectedPrelemIndex}
            dataFromAPI={prelemModelData?.content}
            sectionToUpdate='form_end_date'
            handleSave={handleSave}
            settings={PrelemSettingList[12]}
          />
        )}
        {pageId === PrelemSettingList[13].id && (
          <SingleTextBox
            setPageId={setPageId}
            index={selectedPrelemIndex}
            dataFromAPI={prelemModelData?.content}
            sectionToUpdate='form_id'
            handleSave={handleSave}
            settings={PrelemSettingList[13]}
          />
        )}
        {pageId === PrelemSettingList[14].id && (
          <SingleTextBox
            setPageId={setPageId}
            index={selectedPrelemIndex}
            dataFromAPI={prelemModelData?.content}
            sectionToUpdate='form_version'
            handleSave={handleSave}
            settings={PrelemSettingList[14]}
          />
        )}
        {pageId === PrelemSettingList[15].id && (
          <AdditionalSettings
            setPageId={setPageId}
            index={selectedPrelemIndex}
            sectionToUpdate='additional_setting'
            title='additional_setting'
            data={prelemModelData?.content?.additional_setting}
            handleSave={handleSave}
          />
        )}
        {pageId === PrelemSettingList[16].id && (
          <CardLimitSetting
            setPageId={setPageId}
            index={selectedPrelemIndex}
            sectionToUpdate='select_item_count_x_str'
            title='select_item_count_x_str'
            handleSave={handleSave}
            dataFromAPI={prelemModelData?.content}
          />
        )}
        {pageId === PrelemSettingList[17].id && (
          <GoogleAdSetting
            setPageId={setPageId}
            index={selectedPrelemIndex}
            sectionToUpdate='google_ad_setting'
            handleSave={handleSave}
            googleAds={prelemModelData?.content?.google_ad_setting}
            PrelemId={prelemModelData?.PrelemId}
          />
        )}
        {/* GAM and AdSense share the persisted `google_ad_setting` key: a site runs one provider at a time. */}
        {pageId === "gam_ad_setting" && (
          <GamAdSetting
            setPageId={setPageId}
            index={selectedPrelemIndex}
            sectionToUpdate='google_ad_setting'
            handleSave={handleSave}
            gamAds={prelemModelData?.content?.google_ad_setting}
            PrelemId={prelemModelData?.PrelemId}
          />
        )}
        {pageId === PrelemSettingList[19].id && (
          <SponsorSetting
            setPageId={setPageId}
            index={selectedPrelemIndex}
            sectionToUpdate='sponsor_settings'
            handleSave={handleSave}
            sponsorData={prelemModelData?.content?.sponsor_settings}
            PrelemId={prelemModelData?.PrelemId}
          />
        )}

        {/* Allowed Selection Type dropdown — only shown when
            dynamic_prelem_setting contains allowed_selection_type */}
        {hasAllowedSelectionType && pageId === PrelemSettingList[18].id && (
          <PrelemSettings
            setPageId={setPageId}
            index={selectedPrelemIndex}
            sectionToUpdate='prelem_settings'
            handleSave={handleSave}
            selectedType={dynamicPrelemSetting?.selection_type ?? ""}
            allowedOptions={
              Array.isArray(dynamicPrelemSetting.allowed_selection_type)
                ? dynamicPrelemSetting.allowed_selection_type
                : []
            }
          />
        )}
      </Suspense>
    </Box>
  );
};

export default PrelemSettingsCard;
